// src/pages/finance-portal/expense-reports/ExpenseReports.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, Tabs, Tab,
    InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED: { bg: '#dbeafe', color: '#1e40af' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
    REIMBURSED: { bg: '#ede9fe', color: '#6d28d9' },
    VOID: { bg: '#f1f5f9', color: '#475569' },
};

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Submitted', value: 'SUBMITTED' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Reimbursed', value: 'REIMBURSED' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const defaultNewReport = {
    title: '',
    description: '',
    date_from: '',
    date_to: '',
};

export default function ExpenseReports() {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    // New report dialog
    const [newDialogOpen, setNewDialogOpen] = useState(false);
    const [newReport, setNewReport] = useState(defaultNewReport);
    const [creating, setCreating] = useState(false);

    const fetchReports = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/expense-reports', { params });
            if (res.data.success) {
                setReports(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load expense reports');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusFilter, search]);

    useEffect(() => { fetchReports(); }, [fetchReports]);

    const handleCreateReport = async () => {
        setCreating(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/expense-reports', newReport);
            if (res.data.success) {
                setNewDialogOpen(false);
                setNewReport(defaultNewReport);
                fetchReports();
                // Navigate to the detail page
                if (res.data.data?.id) {
                    navigate(`/finance-portal/expense-reports/${res.data.data.id}`);
                }
            } else {
                setError(res.data.message || 'Failed to create report');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create report');
        } finally {
            setCreating(false);
        }
    };

    const handleRowClick = (report) => {
        navigate(`/finance-portal/expense-reports/${report.id}`);
    };

    const tabIndex = STATUS_TABS.findIndex(t => t.value === statusFilter);
    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SummarizeIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Expense Reports
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Group and submit expenses for approval
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setNewDialogOpen(true)}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        New Report
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search reports..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: fieldSx,
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 280 } }}
                    />
                </Box>

                {/* Status Tabs */}
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, val) => { setStatusFilter(STATUS_TABS[val].value); setPage(0); }}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
                            color: '#64748b', minHeight: 40, py: 1,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' },
                        minHeight: 40,
                    }}
                >
                    {STATUS_TABS.map(tab => (
                        <Tab key={tab.value} label={tab.label} />
                    ))}
                </Tabs>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Report #', 'Title', 'Date Range', '# Expenses', 'Total', 'Reimbursable', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}><Skeleton height={36} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : reports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <SummarizeIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No expense reports found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Create your first expense report to get started</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : reports.map(report => {
                                    const statusStyle = STATUS_COLORS[report.status] || STATUS_COLORS.DRAFT;
                                    return (
                                        <TableRow
                                            key={report.id}
                                            hover
                                            onClick={() => handleRowClick(report)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {report.report_number || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {report.title || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                {report.date_from && report.date_to
                                                    ? `${formatDate(report.date_from)} - ${formatDate(report.date_to)}`
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', textAlign: 'center' }}>
                                                {report.expense_count ?? report.expenses?.length ?? 0}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {formatCurrency(report.total_amount)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {formatCurrency(report.reimbursable_amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={report.status ? report.status.replace(/_/g, ' ') : 'DRAFT'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleRowClick(report); }}
                                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT, fontWeight: 600 }}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* New Report Dialog */}
            <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>New Expense Report</DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small" label="Title" required
                                value={newReport.title}
                                onChange={e => setNewReport(prev => ({ ...prev, title: e.target.value }))}
                                InputProps={{ sx: fieldSx }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small" label="Description"
                                value={newReport.description}
                                onChange={e => setNewReport(prev => ({ ...prev, description: e.target.value }))}
                                multiline rows={2}
                                InputProps={{ sx: fieldSx }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" type="date" label="From Date"
                                value={newReport.date_from}
                                onChange={e => setNewReport(prev => ({ ...prev, date_from: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ sx: fieldSx }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" type="date" label="To Date"
                                value={newReport.date_to}
                                onChange={e => setNewReport(prev => ({ ...prev, date_to: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ sx: fieldSx }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setNewDialogOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateReport}
                        disabled={creating || !newReport.title}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {creating ? 'Creating...' : 'Create Report'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
